import React from "react";
import { type IFiltroTareasVistaProps } from "../interfaces";
import {
    Grid,
    Box,
    Text,
    Button,
    Select,
    TextInput,
} from "@mantine/core";
import { IconClearAll, IconSearch } from "@tabler/icons-react";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";

const FiltroTareasVista: React.FunctionComponent<IFiltroTareasVistaProps> = ({
    consultarTareas,
    filtro,
    manejarCambioFiltroTarea,
    entidadesRegistro,
    limpiarFiltros,
    estadoModalExportacionExcel
}) => {
    return (
        <Box pb="sm">
            <Grid gutter="sm" >
                {estadoModalExportacionExcel && (
                    <Grid.Col span={{ base: 12, md: 3 }}>
                        <DatePickerInput
                            type="range"
                            size="xs"
                            required={!filtro.TareaId}
                            popoverProps={{ zIndex: 1000000 }}
                            valueFormat="MMMM D, YYYY"
                            label="Rango"
                            placeholder="Seleccione Fecha Inicio y Fecha Fin"
                            locale="es"
                            // Mantine espera [Date | null, Date | null] para el tipo "range"
                            value={[
                                filtro.FechaInicio ? new Date(filtro.FechaInicio) : null,
                                filtro.FechaFin ? new Date(filtro.FechaFin) : null
                            ]}
                            onChange={(val) => {
                                // val es [Date | null, Date | null] proporcionado por Mantine
                                const [inicio, fin] = val;

                                // Transformamos a formato ISO (ej: "2026-01-19T04:33:29.596Z")
                                // Usamos startOf y endOf para cubrir el rango completo del día
                                const fechaInicioISO = inicio
                                    ? dayjs(inicio).startOf('day').toISOString()
                                    : '';

                                const fechaFinISO = fin
                                    ? dayjs(fin).endOf('day').toISOString()
                                    : '';

                                // Actualizamos tu estado con las cadenas de texto formateadas
                                manejarCambioFiltroTarea('FechaInicio', fechaInicioISO);
                                manejarCambioFiltroTarea('FechaFin', fechaFinISO);
                            }}
                            clearable
                        />
                    </Grid.Col>
                )}

                <Grid.Col span={{ base: 12, md: estadoModalExportacionExcel ? 3 : 3 }}>
                    <TextInput
                        label="Código Tarea"
                        placeholder="Código Tarea"
                        size="xs"
                        value={filtro.TareaId || ""}
                        onChange={(event) => {
                            const val = event.currentTarget.value;
                            // Solo permitir números
                            if (/^\d*$/.test(val)) {
                                manejarCambioFiltroTarea("TareaId", val ? Number(val) : null);
                            }
                        }}
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: estadoModalExportacionExcel ? 3 : 3 }}>
                    <Select
                        label="Proyecto"
                        placeholder="Seleccionar proyecto..."
                        value={filtro.ProyectoId != null ? String(filtro.ProyectoId) : null}
                        onChange={(value) => {
                            manejarCambioFiltroTarea("ProyectoId", value ? parseInt(value) : null)
                        }}
                        comboboxProps={{ zIndex: 1000000 }}
                        data={entidadesRegistro.Proyectos.map(proyecto => ({
                            value: proyecto.Id.toString(),
                            label: proyecto.Nombre
                        }))}
                        clearable
                        searchable
                        size="xs"
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: estadoModalExportacionExcel ? 3 : 3 }}>
                    <TextInput
                        label="Código Equipo"
                        placeholder="Código Equipo"
                        name="CodigoEquipo"
                        size="xs"
                        maxLength={50}
                        value={filtro.CodigoEquipo || ""}
                        onChange={(event) => {
                            manejarCambioFiltroTarea("CodigoEquipo", event.currentTarget.value)
                        }}
                    />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: estadoModalExportacionExcel ? 3 : 3 }}>
                    <Select
                        label="Tipo Tarea"
                        placeholder="Seleccionar tipo..."
                        value={filtro.TipoServicioId != null ? String(filtro.TipoServicioId) : null}
                        onChange={(value) => {
                            manejarCambioFiltroTarea("TipoServicioId", value ? parseInt(value) : null)
                        }}
                        comboboxProps={{ zIndex: 1000000 }}
                        data={entidadesRegistro.TiposTarea.map(c => ({
                            value: c.Id.toString(),
                            label: c.Nombre,
                        }))}
                        clearable
                        size="xs"
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: estadoModalExportacionExcel ? 3 : 3 }}>
                    <Select
                        label="Técnico"
                        placeholder="Seleccionar técnico..."
                        value={filtro.TecnicoId != null ? String(filtro.TecnicoId) : null}
                        onChange={(value) => {
                            manejarCambioFiltroTarea("TecnicoId", value ? Number(value) : null)
                        }}
                        comboboxProps={{ zIndex: 1000000 }}
                        data={entidadesRegistro.Tecnicos.map(tecnico => ({
                            value: tecnico.Id.toString(),
                            label: tecnico.NombreCompleto
                        }))}
                        clearable
                        searchable
                        size="xs"
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: estadoModalExportacionExcel ? 3 : 3 }}>
                    <Select
                        label="Estado"
                        placeholder="Seleccionar estado..."
                        value={filtro.EstadoId != null ? String(filtro.EstadoId) : null}
                        onChange={(value) => {
                            manejarCambioFiltroTarea("EstadoId", value ? parseInt(value) : null)
                        }}
                        data={entidadesRegistro.EstadosTarea.map(estado => ({
                            value: estado.Id.toString(),
                            label: estado.Nombre
                        }))}
                        comboboxProps={{ zIndex: 1000000 }}
                        searchable
                        clearable
                        size="xs"
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: estadoModalExportacionExcel ? 3 : 3 }}>
                    <Select
                        label="País"
                        placeholder="Seleccionar país..."
                        searchable
                        size="xs"
                        clearable
                        value={filtro.PaisId != null ? String(filtro.PaisId) : null}
                        onChange={(value) => {
                            manejarCambioFiltroTarea("PaisId", value ? parseInt(value) : null)
                        }}
                        comboboxProps={{ zIndex: 1000000 }}
                        data={entidadesRegistro.Paises ? entidadesRegistro.Paises.map(pais => ({
                            value: pais.Id.toString(),
                            label: pais.Nombre
                        })) : []}
                    />
                </Grid.Col>

                <Grid.Col
                    span={{ base: 12, md: estadoModalExportacionExcel ? 3 : 3 }}
                    style={{
                        display: "flex",
                        alignItems: estadoModalExportacionExcel ? "center" : "flex-end",
                        justifyContent: "flex-end",
                        gap: "0.5rem",
                        marginTop: estadoModalExportacionExcel ? "1.7rem" : 0
                    }}
                >
                    <Button
                        onClick={limpiarFiltros}
                        color="primary"
                        variant="light"
                        fullWidth={true}
                        size="compact-sm"
                        radius="lg"
                        rightSection={<IconClearAll opacity={1} size={12} />}
                    >
                        <Text fw={500} fz={11}>Limpiar</Text>
                    </Button>

                    <Button
                        onClick={consultarTareas}
                        color="primary"
                        variant="filled"
                        fullWidth={true}
                        size="compact-sm"
                        radius="lg"
                        rightSection={<IconSearch opacity={1} size={12} />}
                    >
                        <Text fw={500} fz={11}>Consultar</Text>
                    </Button>
                </Grid.Col>

            </Grid>
        </Box>
    );
}

export default FiltroTareasVista;
